import axios from 'axios';

const API_BASE = '/api/v1/auth';

// Create a dedicated axios instance for engineer auth with credentials.
// withCredentials: true ensures httpOnly cookies (access_token, refresh_token,
// session_token) are sent automatically — no localStorage required.
const engineerAxios = axios.create({
  withCredentials: true,
});

// Apply withCredentials to the global axios instance too so that all
// existing API calls made via bare `axios` also send the engineer cookies.
axios.defaults.withCredentials = true;

export interface AuthResponse {
  user?: {
    id: string;
    email: string;
    name: string;
    role: string;
  };
  // NOTE: accessToken and refreshToken are NO LONGER returned in the response
  // body.  They are delivered as httpOnly cookies by the server.
  twoFactorRequired?: boolean;
  token_type?: string;
  expires_in?: number;
  // engineer object from GitHubCallbackResponse
  engineer?: {
    id: string;
    engineer_id: string;
    name: string;
    email: string;
    role: string;
    organization: string;
    last_login_at?: string;
  };
  success?: boolean;
}

export const authAPI = {
  githubLogin: () => {
    window.location.href = `${API_BASE}/github`;
  },

  githubCallback: async (code: string) => {
    return engineerAxios.get<AuthResponse>(`${API_BASE}/github/callback`, {
      params: { code },
    });
  },

  verify2FA: async (code: string) => {
    return engineerAxios.post<AuthResponse>(`${API_BASE}/2fa/verify`, { code });
  },

  // Refresh: sends httpOnly cookie automatically (withCredentials: true).
  // No request body needed for browser sessions.
  refreshToken: async () => {
    return engineerAxios.post<AuthResponse>(`${API_BASE}/refresh`);
  },

  // Logout: calls server to clear httpOnly cookies.
  logout: async () => {
    return engineerAxios.post(`${API_BASE}/logout`);
  },
};

// ============================================================================
// Axios interceptors (global instance used by legacy call sites)
// ============================================================================
// REMOVED: the interceptor that injected Authorization: Bearer <token> from
// localStorage.  Browser sessions now rely on httpOnly cookies sent
// automatically by the browser (withCredentials: true above).
// CLI / API clients use the Authorization header via their own HTTP clients.
// ============================================================================

// Add interceptor for automatic token refresh on 401
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Refresh via cookie — no tokens in JS
        await authAPI.refreshToken();
        // Retry the original request; the new access_token cookie will be
        // sent automatically.
        return axios(originalRequest);
      } catch (refreshError) {
        // Refresh failed — redirect to login
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);
